import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { HeroId } from '../game/data/heroes';

// Local-first account store. Persists to localStorage (zustand/persist).
// Real cloud sync wires in R9 (Better Auth + cve-api).
//
// Stats are incremented by ResultsPage when a match completes via
// recordMatchResult(). Hero play counts feed the "favorite hero" derivation.

/** User preference for reduced motion. 'auto' defers to the OS setting. */
export type ReducedMotionPref = 'auto' | 'on' | 'off';

/** Supported languages. en is the source of truth; es is the only translated locale for v2 launch. */
export type LanguageCode = 'en' | 'es';

interface AccountState {
  // Onboarding gate — flipped true after first /welcome → /menu click.
  hasPlayed: boolean;
  // Onboarding modal — flipped true after the 3-slide tutorial is dismissed.
  // Separate from hasPlayed so we can replay the tutorial from Settings later.
  hasSeenOnboarding: boolean;
  // Display name (editable in Profile). Defaults to a flavor name; user can change.
  handle: string;
  // Allegiance toggle — purely cosmetic for now; future cosmetics tier off it.
  factionAllegiance: 'cholo' | 'emo' | null;
  // Match counters
  totalMatches: number;
  wins: number;
  // Per-hero play counts — feeds favorite hero derivation
  heroPlayCounts: Partial<Record<HeroId, number>>;
  // --- Settings (audio + language + motion) ---
  // Volume sliders are 0–100 ints. Audio not yet wired up; values persist
  // for when @pixi/sound integration lands.
  audioMaster: number;
  audioMusic: number;
  audioSfx: number;
  language: LanguageCode;
  reducedMotionPref: ReducedMotionPref;
  /** Skip the combat-canvas playback entirely and jump to the outcome. */
  autoSkipCombat: boolean;
  /** Index into PLAYLIST of the last track the player heard — used to
      resume from there on the next session. -1 = play from start. */
  lastPlayedTrackIdx: number;

  // Actions
  setHasPlayed: (v: boolean) => void;
  setHasSeenOnboarding: (v: boolean) => void;
  setHandle: (handle: string) => void;
  setFactionAllegiance: (f: 'cholo' | 'emo' | null) => void;
  recordMatchResult: (heroId: HeroId, placement: number | null) => void;
  resetStats: () => void;
  // Settings actions
  setAudioMaster: (v: number) => void;
  setAudioMusic: (v: number) => void;
  setAudioSfx: (v: number) => void;
  setLanguage: (l: LanguageCode) => void;
  setReducedMotionPref: (p: ReducedMotionPref) => void;
  setAutoSkipCombat: (v: boolean) => void;
  setLastPlayedTrackIdx: (i: number) => void;
  replayTutorial: () => void;
}

const DEFAULT_HANDLE = 'Carnal Anónimo';

// Schema version. Bump when persisted shape changes incompatibly so the
// migrate hook can clear stale entries instead of crashing the hydration.
const ACCOUNT_SCHEMA_VERSION = 1;

const DEFAULT_STATE = {
  hasPlayed: false,
  hasSeenOnboarding: false,
  handle: DEFAULT_HANDLE,
  factionAllegiance: null as 'cholo' | 'emo' | null,
  totalMatches: 0,
  wins: 0,
  heroPlayCounts: {} as Partial<Record<HeroId, number>>,
  audioMaster: 80,
  audioMusic: 60,
  audioSfx: 80,
  autoSkipCombat: false,
  lastPlayedTrackIdx: -1,
  language: 'en' as LanguageCode,
  reducedMotionPref: 'auto' as ReducedMotionPref,
};

export const useAccountStore = create<AccountState>()(
  persist(
    (set) => ({
      ...DEFAULT_STATE,

      setHasPlayed: (v) => set({ hasPlayed: v }),
      setHasSeenOnboarding: (v) => set({ hasSeenOnboarding: v }),
      setHandle: (handle) => set({ handle: handle.trim().slice(0, 24) || DEFAULT_HANDLE }),
      setFactionAllegiance: (factionAllegiance) => set({ factionAllegiance }),
      recordMatchResult: (heroId, placement) =>
        set((s) => ({
          totalMatches: s.totalMatches + 1,
          wins: placement === 1 ? s.wins + 1 : s.wins,
          heroPlayCounts: {
            ...s.heroPlayCounts,
            [heroId]: (s.heroPlayCounts[heroId] ?? 0) + 1,
          },
        })),
      resetStats: () =>
        set({ totalMatches: 0, wins: 0, heroPlayCounts: {} }),
      // Sliders clamp to [0, 100] and round to int so the persisted value
      // is comparable across reloads without floating-point drift.
      setAudioMaster: (v) => set({ audioMaster: clampVolume(v) }),
      setAudioMusic: (v) => set({ audioMusic: clampVolume(v) }),
      setAudioSfx: (v) => set({ audioSfx: clampVolume(v) }),
      setLanguage: (language) => set({ language }),
      setReducedMotionPref: (reducedMotionPref) => set({ reducedMotionPref }),
      setAutoSkipCombat: (autoSkipCombat) => set({ autoSkipCombat }),
      setLastPlayedTrackIdx: (lastPlayedTrackIdx) => set({ lastPlayedTrackIdx }),
      replayTutorial: () => set({ hasSeenOnboarding: false }),
    }),
    {
      name: 'cve.v2.account',
      version: ACCOUNT_SCHEMA_VERSION,
      // Defensive merge: any persisted field that doesn't deserialize to
      // the expected type is dropped in favor of the default. This means a
      // single corrupt localStorage entry (manually edited, browser bug,
      // schema drift) can't crash the entire app — at worst the user
      // loses just the bad field.
      merge: (persisted, current) => {
        const safeMerged = { ...current };
        if (!persisted || typeof persisted !== 'object') return safeMerged;
        const p = persisted as Record<string, unknown>;
        if (typeof p.hasPlayed === 'boolean') safeMerged.hasPlayed = p.hasPlayed;
        if (typeof p.hasSeenOnboarding === 'boolean') safeMerged.hasSeenOnboarding = p.hasSeenOnboarding;
        if (typeof p.handle === 'string') safeMerged.handle = p.handle;
        if (p.factionAllegiance === 'cholo' || p.factionAllegiance === 'emo') {
          safeMerged.factionAllegiance = p.factionAllegiance;
        } else if (p.factionAllegiance === null) {
          safeMerged.factionAllegiance = null;
        }
        if (typeof p.totalMatches === 'number' && Number.isFinite(p.totalMatches)) {
          safeMerged.totalMatches = Math.max(0, Math.floor(p.totalMatches));
        }
        if (typeof p.wins === 'number' && Number.isFinite(p.wins)) {
          safeMerged.wins = Math.max(0, Math.floor(p.wins));
        }
        if (p.heroPlayCounts && typeof p.heroPlayCounts === 'object') {
          safeMerged.heroPlayCounts = p.heroPlayCounts as Partial<Record<HeroId, number>>;
        }
        if (typeof p.audioMaster === 'number') safeMerged.audioMaster = clampVolume(p.audioMaster);
        if (typeof p.audioMusic === 'number') safeMerged.audioMusic = clampVolume(p.audioMusic);
        if (typeof p.audioSfx === 'number') safeMerged.audioSfx = clampVolume(p.audioSfx);
        if (p.language === 'en' || p.language === 'es') safeMerged.language = p.language;
        if (p.reducedMotionPref === 'auto' || p.reducedMotionPref === 'on' || p.reducedMotionPref === 'off') {
          safeMerged.reducedMotionPref = p.reducedMotionPref;
        }
        if (typeof p.autoSkipCombat === 'boolean') safeMerged.autoSkipCombat = p.autoSkipCombat;
        if (typeof p.lastPlayedTrackIdx === 'number' && Number.isInteger(p.lastPlayedTrackIdx)) {
          safeMerged.lastPlayedTrackIdx = p.lastPlayedTrackIdx;
        }
        return safeMerged;
      },
      // If the persisted version is older, treat the stored blob as
      // unknown — `merge` will then drop unrecognized shapes. Future
      // breaking changes should bump ACCOUNT_SCHEMA_VERSION.
      migrate: (persisted, version) => {
        if (version === ACCOUNT_SCHEMA_VERSION) return persisted as Partial<AccountState>;
        return DEFAULT_STATE as Partial<AccountState>;
      },
    },
  ),
);

function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, Math.round(v)));
}

/** Derive favorite hero id from heroPlayCounts. Returns null if no matches played. */
export function deriveFavoriteHero(counts: Partial<Record<HeroId, number>>): HeroId | null {
  let topId: HeroId | null = null;
  let topCount = 0;
  for (const [id, count] of Object.entries(counts) as [HeroId, number][]) {
    if (count > topCount) {
      topId = id;
      topCount = count;
    }
  }
  return topId;
}
